// Floating properties panel — with many layers in the sidebar, an inline edit area gets pushed
// off-screen (bad ergonomics). Extracted from SidePanel, floats over the top-right of the canvas;
// shown only when something is selected; closes via X or clicking blank canvas (deselect).
// Content = the former SidePanel's "multi-select actions + single-select props" sections, logic unchanged.

import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { FALLBACK_FONTS, loadLocalFonts, localFontsSupported } from '../lib/fonts';
import {
  XIcon, CropIcon, ChevronUpIcon, ChevronDownIcon,
  AlignLeftIcon, AlignHCenterIcon, AlignRightIcon,
  AlignTopIcon, AlignVCenterIcon, AlignBottomIcon,
} from './icons';
import type { CanvasObject, JobRecord, Page } from '../types';

export function toHex7(c: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(c) ? c : '#1a1a1a';
}

export interface PropsFloatProps {
  objects: CanvasObject[];
  selectedId: string | null;
  selectedIds: string[];
  jobs: Record<string, JobRecord>;
  onPatch: (id: string, patch: Partial<CanvasObject>) => void;
  onRetryJob: (id: string) => void;
  onRegionEdit: (id: string) => void;
  pages: Page[];
  currentPageId: string | null;
  onGroup: () => void;
  onUngroup: () => void;
  onDeleteSelected: () => void;
  /** Multi-select alignment */
  onAlign: (dir: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom') => void;
  onClose: () => void;
  /** Collapse/expand — shrink to a small bar when the panel blocks canvas dragging */
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function PropsFloat({ objects, selectedId, selectedIds, jobs, onPatch, onRetryJob, onRegionEdit, pages, currentPageId, onGroup, onUngroup, onDeleteSelected, onAlign, onClose, collapsed, onToggleCollapse }: PropsFloatProps) {
  const t = useT();
  const multi = selectedIds.length > 1;
  const selected = !multi ? (objects.find(o => o.id === selectedId) ?? null) : null;
  const anyGrouped = multi && objects.some(o => selectedIds.includes(o.id) && o.group_id);
  const [fonts, setFonts] = useState<string[]>(FALLBACK_FONTS);
  const [fontsHint, setFontsHint] = useState<'idle' | 'denied' | 'ok'>('idle');

  // When a text object is selected, try loading local fonts (needs a user gesture; the first select click after mount also triggers it)
  useEffect(() => {
    if (selected?.type !== 'text') return;
    let alive = true;
    loadLocalFonts().then(f => { if (alive) { setFonts(f); setFontsHint(f.length > FALLBACK_FONTS.length ? 'ok' : (localFontsSupported() ? 'idle' : 'denied')); } });
    return () => { alive = false; };
  }, [selected?.type, selected?.id]);

  // Collapsed state = a small bar at the same position, freeing up the canvas.
  // ⚠️ Must come after all hooks — an early return would change the hook count and React would crash the whole tree (white screen).
  if (collapsed) {
    return (
      <div className="props-float props-float-collapsed">
        <button className="props-float-collapsed-btn" onClick={onToggleCollapse} title={t('side.props.expand')} aria-label={t('side.props.expand')}>
          <ChevronDownIcon /> <span>{multi ? t('side.multi.title', { n: selectedIds.length }) : t('side.props')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="props-float">
      <div className="props-float-head">
        <h4 className="panel-title">{multi ? t('side.multi.title', { n: selectedIds.length }) : t('side.props')}</h4>
        <button className="props-float-close" onClick={onToggleCollapse} title={t('side.props.collapse')} aria-label={t('side.props.collapse')}>
          <ChevronUpIcon />
        </button>
        <button className="props-float-close" onClick={onClose} title={t('side.props.close')} aria-label={t('side.props.close')}>
          <XIcon />
        </button>
      </div>

      {multi && (
        <>
          <div className="align-row" role="toolbar" aria-label={t('side.alignTo.title')}>
            <button title={t('side.alignTo.left')} aria-label={t('side.alignTo.left')} onClick={() => onAlign('left')}><AlignLeftIcon /></button>
            <button title={t('side.alignTo.hcenter')} aria-label={t('side.alignTo.hcenter')} onClick={() => onAlign('hcenter')}><AlignHCenterIcon /></button>
            <button title={t('side.alignTo.right')} aria-label={t('side.alignTo.right')} onClick={() => onAlign('right')}><AlignRightIcon /></button>
            <button title={t('side.alignTo.top')} aria-label={t('side.alignTo.top')} onClick={() => onAlign('top')}><AlignTopIcon /></button>
            <button title={t('side.alignTo.vcenter')} aria-label={t('side.alignTo.vcenter')} onClick={() => onAlign('vcenter')}><AlignVCenterIcon /></button>
            <button title={t('side.alignTo.bottom')} aria-label={t('side.alignTo.bottom')} onClick={() => onAlign('bottom')}><AlignBottomIcon /></button>
          </div>
          <div className="multi-actions">
            <button className="ghost-btn" onClick={onGroup}>{t('side.group')}</button>
            <button className="ghost-btn" onClick={onUngroup} disabled={!anyGrouped}>{t('side.ungroup')}</button>
            <button className="danger-btn" onClick={onDeleteSelected}>{t('side.deleteSelected')}</button>
          </div>
        </>
      )}

      {selected && (
        <>
          {selected.type === 'text' && (
            <>
              <label className="field">{t('side.content')}
                <textarea className="text-input" rows={3} value={selected.text ?? ''} onChange={e => onPatch(selected.id, { text: e.target.value })} />
              </label>
              <label className="field">{t('side.fontSize')}
                <input className="text-input" type="number" min={10} max={400} value={selected.font_size ?? 48} onChange={e => onPatch(selected.id, { font_size: Number(e.target.value) || 48, h: (Number(e.target.value) || 48) * 1.4 })} />
              </label>
              <label className="field">{t('side.fontFamily')}
                <select
                  className="text-input"
                  value={selected.font_family ?? ''}
                  onFocus={() => { void loadLocalFonts().then(setFonts); }}
                  onChange={e => onPatch(selected.id, { font_family: e.target.value || undefined })}
                >
                  <option value="">{t('side.fontFamily.default')}</option>
                  {fonts.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                {fontsHint === 'denied' && <span className="hint fonts-hint">{t('side.fontFamily.unsupported')}</span>}
              </label>
              <label className="field">{t('side.color')}
                <input className="text-input color-input" type="color" value={toHex7(selected.color ?? '#1a1a1a')} onChange={e => onPatch(selected.id, { color: e.target.value })} />
              </label>
              <label className="field">{t('side.weight')}
                <select className="text-input" value={selected.font_weight ?? 'normal'} onChange={e => onPatch(selected.id, { font_weight: e.target.value as 'normal' | 'bold' })}>
                  <option value="normal">{t('side.weight.normal')}</option>
                  <option value="bold">{t('side.weight.bold')}</option>
                </select>
              </label>
              <label className="field">{t('side.align')}
                <select className="text-input" value={selected.align ?? 'left'} onChange={e => onPatch(selected.id, { align: e.target.value as 'left' | 'center' | 'right' })}>
                  <option value="left">{t('side.align.left')}</option>
                  <option value="center">{t('side.align.center')}</option>
                  <option value="right">{t('side.align.right')}</option>
                </select>
              </label>
            </>
          )}
          {selected.type === 'image' && (
            <>
              <p className="hint mono-readout">{Math.round(selected.w)}×{Math.round(selected.h)} · ({Math.round(selected.x)}, {Math.round(selected.y)})</p>
              {/* Shape mask: rounded corners + ellipse */}
              <label className="field">{t('side.mask')}
                <select
                  className="text-input"
                  value={selected.mask ?? ((selected.radius ?? 0) > 0 ? 'radius' : 'none')}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === 'ellipse') onPatch(selected.id, { mask: 'ellipse' });
                    else if (v === 'radius') onPatch(selected.id, { mask: undefined, radius: (selected.radius ?? 0) > 0 ? selected.radius : 24 });
                    else onPatch(selected.id, { mask: undefined, radius: 0 });
                  }}
                >
                  <option value="none">{t('side.mask.none')}</option>
                  <option value="radius">{t('side.mask.radius')}</option>
                  <option value="ellipse">{t('side.mask.ellipse')}</option>
                </select>
              </label>
              {selected.mask !== 'ellipse' && (
                <label className="field">{t('side.rect.radius')}
                  <input className="text-input" type="number" min={0} max={400} value={selected.radius ?? 0} onChange={e => onPatch(selected.id, { radius: Math.max(0, Number(e.target.value) || 0), mask: undefined })} />
                </label>
              )}
              {selected.src && !selected.pending && !((selected.rotation ?? 0) % 360) && (
                <button className="ghost-btn region-edit-btn" onClick={() => onRegionEdit(selected.id)}>
                  <CropIcon /> {t('side.regionEdit')}
                </button>
              )}
              {selected.error && selected.job_id && (
                <button className="danger-btn retry-gen" onClick={() => onRetryJob(selected.job_id!)}>{t('side.retryGen')}</button>
              )}
              {selected.job_id && jobs[selected.job_id]?.error && (
                <p className="error-text">{jobs[selected.job_id].error}</p>
              )}
            </>
          )}
          {selected.type === 'rect' && (
            <>
              <p className="hint mono-readout">{Math.round(selected.w)}×{Math.round(selected.h)} · ({Math.round(selected.x)}, {Math.round(selected.y)})</p>
              <label className="field">{t('side.rect.fill')}
                <input className="text-input color-input" type="color" value={toHex7(selected.fill ?? '#808080')} onChange={e => onPatch(selected.id, { fill: e.target.value })} />
              </label>
              <label className="field">{t('side.rect.radius')}
                <input className="text-input" type="number" min={0} max={200} value={selected.radius ?? 8} onChange={e => onPatch(selected.id, { radius: Math.max(0, Number(e.target.value) || 0) })} />
              </label>
              <label className="field">{t('side.rect.stroke')}
                <input className="text-input color-input" type="color" value={toHex7(selected.stroke ?? '#d4d4d8')} onChange={e => onPatch(selected.id, { stroke: e.target.value, stroke_width: selected.stroke_width || 1 })} />
              </label>
              <label className="field">{t('side.rect.strokeWidth')}
                <input className="text-input" type="number" min={0} max={20} value={selected.stroke_width ?? 0} onChange={e => onPatch(selected.id, { stroke_width: Math.max(0, Number(e.target.value) || 0) })} />
              </label>
            </>
          )}
          {/* SPEC-457 M1: icon 对象 — 单色矢量图标，仅暴露描边色（color 兼任 stroke color）；
              图标名/几何不可改（换图标 = 删了重加，M1 不做 swap 编辑器）。 */}
          {selected.type === 'icon' && (
            <>
              <p className="hint mono-readout">{Math.round(selected.w)}×{Math.round(selected.h)} · ({Math.round(selected.x)}, {Math.round(selected.y)})</p>
              <label className="field">{t('side.icon.color')}
                <input className="text-input color-input" type="color" value={toHex7(selected.color ?? '#1a1a1a')} onChange={e => onPatch(selected.id, { color: e.target.value })} />
              </label>
            </>
          )}
          {pages.length > 1 && (
            <label className="field">{t('side.linkTo')}
              <select className="text-input" value={selected.link_to ?? ''} onChange={e => onPatch(selected.id, { link_to: e.target.value || undefined })}>
                <option value="">{t('side.linkTo.none')}</option>
                {pages.filter(pg => pg.id !== currentPageId).map(pg => (
                  <option key={pg.id} value={pg.id}>{pg.name}</option>
                ))}
              </select>
            </label>
          )}
        </>
      )}
    </div>
  );
}
